// React
import React from 'react';
// External
import { Form, Col } from 'react-bootstrap';
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete';
// Internal
import getFormFieldVal from 'utils/getFormFieldVal';
import Owner from 'models/Owner';
import Address from 'models/Address';
import config from 'config/config';
// Styles
import './styles.scss';
import { BookingContext } from 'context/BookingContext';

/**
 * @param {string} props.data - The text to be display in the previous button
 * @todo Change select input to customise the select options (Style)
 */
class OwnerDetailsForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            formData: Owner(this.props.formData),
            addressMenuIsOpen: false,
            hideForm: this.props.hideForm,
            errors: [],
            input: this.props.formData.address ? this.props.formData.address.getSingleLineAddress() : '',
        };
    }

    setInput(input) {
        this.setState({input: input})
    }

    handleInputChange(event) {
        const fieldObj = getFormFieldVal(event);
        // Updates the state
        this.setState((prevState) => {
            const formData = Owner({ ...prevState.formData, ...fieldObj });
            const errIndex = prevState.errors.findIndex(error => error.field === event.target.name);
            if (errIndex >= 0) {
                const { errors } = prevState;
                errors.splice(errIndex, 1);
                return { formData, errors };
            }

            // Only trigger the last name change prop event for owner last name changes
            if (event.target.name === 'lastName') {
                this.props.onLastNameChange(event);
            }

            return { formData };
        });
    }

    handlePhoneInputChange(event) {
        const { phoneNumber } = getFormFieldVal(event);
        const numberRegEx = /^[0-9]*$/;
        if (numberRegEx.test(phoneNumber)) {
            this.handleInputChange(event);
        }
    }

    /**
     * Handles skip owner details checkbox event
     * 
     * @param {Boolean} bHide 
     */
    handleCheckbox(bHide) {
        this.setState({
            hideForm: bHide,
        });
    }

    /**
     * Function call from the parent component
     */
    async handleSubmit() {
        let errors = [];
        await this.setState((prevState) => {
            errors = prevState.formData.validateRequiredData();
            return { errors };
        });
        // Returns the data if the form has no errors
        return errors.length > 0 ? null : this.state.formData;
    }

    onAddressSelect(addressObj, _BookingContext) {
        if (!addressObj) {
            this.setState((prevState) => {
                const formData = { ...prevState.formData, address: null };
                return { formData };
            });
            return;
        }
        const placeId = addressObj.value.place_id;
        geocodeByPlaceId(placeId)
            .then((result) => {
                if (!result.length) {
                    // Sets the error in the field
                    this.setState((prevState) => {
                        const { errors } = prevState;
                        this.addError(errors, 'address', 'Error in selecting address');
                        return { errors };
                    });
                }
                let address = {
                    street: addressObj.value.structured_formatting.main_text,
                    lat: result[0].geometry.location.lat(),
                    lng: result[0].geometry.location.lng()
                };

                result[0].address_components.forEach((element) => {
                    // Subpremise or unit object path
                    if (element.types.includes('subpremise') && !address.street.includes(element.short_name)) {
                        address.street =  `${element.short_name}/` + address.street;
                    }

                    // City object path
                    if (element.types.includes('locality')) {
                        address.city = element.long_name;
                    }
                    // State object path
                    if (element.types.includes('administrative_area_level_1')) {
                        address.state = element.short_name;
                    }
                    // Country object path
                    if (element.types.includes('country')) {
                        address.country = element.long_name;
                    }
                    // Postal code object path
                    if (element.types.includes('postal_code')) {
                        address.postalcode = element.long_name;
                    }
                });

                if (!address.city) {
                    this.setState((prevState) => {
                        const { errors } = prevState;
                        this.addError(errors, 'address_city', 'City not found');
                        return { errors };
                    });
                } else {
                    this.setState((prevState) => {
                        const { errors } = prevState;
                        const errIndex = prevState.errors.findIndex(error => error.field === 'address_city');
                        if (errIndex >= 0) {
                            errors.splice(errIndex, 1);
                        }
                        return { errors };
                    });
                }

                if (!address.postalcode) {
                    this.setState((prevState) => {
                        const { errors } = prevState;
                        this.addError(errors, 'address_postalcode', 'Postal code not found');
                        return { errors };
                    });
                } else {
                    this.setState((prevState) => {
                        const { errors } = prevState;
                        const errIndex = prevState.errors.findIndex(error => error.field === 'address_postalcode');
                        if (errIndex >= 0) {
                            errors.splice(errIndex, 1);
                        }
                        return { errors };
                    });
                }

                address = Address(address);
                this.setState((prevState) => {
                    const formData = { ...prevState.formData, address };
                    const errIndex = prevState.errors.findIndex(error => error.field === 'address');
                    if (errIndex >= 0) {
                        const { errors } = prevState;
                        errors.splice(errIndex, 1);
                        return { formData, errors };
                    }
                    return { formData };
                });

                _BookingContext && _BookingContext.setState(prev => {
                    return {
                        ...prev,
                        owner: {
                            ...prev.owner,
                            address: address
                        },
                        collectionDetails: {
                            ...prev.collectionDetails,
                            collectionProducts: prev.collectionDetails.collectionAddressOption === 'customer_address' ? [] : prev.collectionDetails.collectionProducts
                        }
                    };
                });

                

            })
            .catch((error) => console.error(error));
    }

    hasError(fieldError) {
        return this.state.errors.some(error => error.field === fieldError);
    }

    /**
     * Get the provided field's error message
     * 
     * @param {string} fieldError 
     * @returns {string}
     */
    getError(fieldError) {
        const error = this.state.errors.filter(error => error.field === fieldError);

        if (error.length !== 0) {
            return error[0].message;
        }
    }

    /**
     * Adds an error to the error array
     * 
     * @param {array} errors 
     * @param {string} field 
     * @param {string} message 
     * @returns {void}
     */
     addError(errors, field, message) {
        const errIndex = errors.findIndex(error => error.field === field);

        if (errIndex < 0) {
            errors.push({
                field: field,
                message: message
            });
        } else {
            errors[errIndex].message = message;
        }
    }

    render() {
        return (
            !this.state.hideForm ? 
            <BookingContext.Consumer>
                {_BookingContext => 
                    <Form className="owner-custom-inline-form">
                        <Form.Row>
                            <Form.Group as={Col} controlId="firstName" className="control-group">
                                <Form.Label className={this.hasError('firstName') ? 'is-invalid' : ''}>OWNER FIRST NAME *</Form.Label>
                                <Form.Control
                                    className={this.hasError('firstName') ? 'is-invalid' : ''}
                                    name="firstName"
                                    onChange={(event) => this.handleInputChange(event)}
                                    type="text"
                                    value={this.state.formData.firstName}
                                    disabled={this.props.ownerDetailsDisabled}
                                />
                            </Form.Group>
                            <Form.Group as={Col} controlId="lastName" className="control-group">
                                <Form.Label className={this.hasError('lastName') ? 'is-invalid' : ''}>OWNER LAST NAME *</Form.Label>
                                <Form.Control
                                    className={this.hasError('lastName') ? 'is-invalid' : ''}
                                    name="lastName"
                                    onChange={(event) => this.handleInputChange(event)}
                                    type="text"
                                    value={this.state.formData.lastName}
                                    onBlur={e => this.props.onAutoPopulate(e)}
                                    disabled={this.props.ownerDetailsDisabled}
                                />
                            </Form.Group>
                        </Form.Row>
                        <Form.Row>
                            <Form.Group as={Col} controlId="phone-number" className="control-group">
                                <Form.Label className={this.hasError('phoneNumber') ? 'is-invalid' : ''}>PHONE NUMBER *</Form.Label>
                                <Form.Control
                                    className={this.hasError('phoneNumber') ? 'is-invalid' : ''}
                                    name="phoneNumber"
                                    onChange={(event) => this.handlePhoneInputChange(event)}
                                    type="text"
                                    placeholder="Valid mobile or phone with area code"
                                    minLength="10"
                                    maxLength="10"
                                    value={this.state.formData.phoneNumber}
                                    disabled={this.props.ownerDetailsDisabled}
                                />
                                <Form.Control.Feedback type="invalid">Please enter valid Phone Number</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group as={Col} controlId="email" className="control-group">
                                <Form.Label className={this.hasError('email') ? 'is-invalid' : ''}>EMAIL ADDRESS</Form.Label>
                                <Form.Control
                                    className={this.hasError('email') ? 'is-invalid' : ''}
                                    name="email"
                                    onChange={(event) => this.handleInputChange(event)}
                                    type="text"
                                    value={this.state.formData.email}
                                    disabled={this.props.ownerDetailsDisabled}
                                />
                                <Form.Control.Feedback type="invalid">{this.getError('email')}</Form.Control.Feedback>
                            </Form.Group>
                        </Form.Row>
                        <Form.Row>
                            <Form.Group as={Col} md="6" controlId="address" className="control-group pl-0">
                                <Form.Label className={this.hasError('address') ? 'is-invalid' : ''}>HOME ADDRESS *</Form.Label>
                                {
                                    this.props.ownerDetailsDisabled && this.state.input ?
                                    <Form.Control
                                        className={this.hasError('address') ? 'is-invalid' : ''}
                                        name="address"
                                        type="text"
                                        value={this.state.input}
                                        disabled={this.props.ownerDetailsDisabled}
                                        style={{ width: '100%' }}
                                    /> :
                                <GooglePlacesAutocomplete
                                    apiKey={config.GOOGLE_MAPS_KEY}
                                    apiOptions={{ language: 'en', region: 'au' }}
                                    minLengthAutocomplete="3"
                                    selectProps={{
                                        onChange: (value) => { this.onAddressSelect(value, _BookingContext); },
                                        onInputChange: (value, action) => {
                                            if (action?.action !== 'input-blur' && action?.action !== 'menu-close') {
                                            this.setState({ input: value });
                                            } 
                                            if (value) {
                                                this.onAddressSelect('', _BookingContext);
                                            }
                                            this.setState((prevState) => {
                                                const errIndex = prevState.errors.findIndex(error => error.field === 'address');
                                                if (errIndex >= 0) {
                                                    const { errors } = prevState;
                                                    errors.splice(errIndex, 1);
                                                    return { addressMenuIsOpen: value.length >= 3, errors };
                                                }
                                                return { addressMenuIsOpen: value.length >= 3 };
                                            });
                                        },
                                        className: 'address-select-container ' + this.hasError('address') ? 'is-invalid' : '', 
                                        classNamePrefix: 'address-select',
                                        menuIsOpen: this.state.addressMenuIsOpen,
                                        inputValue: this.state.input,
                                        styles: {
                                            control: (provided, { isFocused }) => ({
                                                ...provided,
                                                fontWeight: '300',
                                                fontSize: '18px',
                                                lineHeight: '22px',
                                                minWidth: '380px',
                                                minHeight: '60px',
                                                maxHeight: '60px',
                                                height: '60px',
                                                borderRadius: '4px',
                                                backgroundColor: this.props.ownerDetailsDisabled ? '#E9ECEF' : '#fff',
                                                border: `solid ${this.hasError('address') ? '2px #dc3545' : '1px #77878A'} !important`,
                                                // is Focus
                                                borderColor: this.hasError('address') ? '#dc3545' : (isFocused ? '#77878A' : 'null'),
                                                outline: 0,
                                                // eslint-disable-next-line no-nested-ternary
                                                boxShadow: isFocused
                                                    ? this.hasError('address')
                                                        ? '0 0 0 0.2rem rgb(220 53 69 / 25%)' : '0 0 0 0.1rem #5eb5c1'
                                                    : null
                                            })
                                        },
                                        placeholder: '',
                                    }}
                                />
                            }
                            </Form.Group>
                            {/* <Form.Group as={Col} controlId="return-to" className="control-group">
                                <Form.Label>RETURN TO</Form.Label>
                                <Selector
                                    options={RETURN_TO_OPTIONS}
                                    name="returnTo"
                                    controlClassName={this.hasError('returnTo') ? 'is-invalid' : ''}
                                    hasError={this.hasError('returnTo')}
                                    onChange={(value) => {
                                        // Updates the state
                                        this.setState((prevState) => {
                                            const formData = { ...prevState.formData, ...{ returnTo: value } };
                                            return { formData };
                                        });
                                    }}
                                    value={this.state.formData.returnTo}
                                />
                            </Form.Group> */}
                        </Form.Row>
                    </Form>
                }
            </BookingContext.Consumer>
             : null
        );
    }
}

export default OwnerDetailsForm;
